/*
 * Config-driven A/B experiment client.
 *
 * Mirrors the deterministic assignment of the analysis pipeline
 * (qlearn-unified scripts/qlearn-experiment-assign.mjs): the variant is
 * sha256("<key>:<salt>:<unitId>") → a 52-bit integer → mod 10000 → bucketed by
 * cumulative variant weight. The port is parity-locked byte-for-byte by test
 * vectors generated from that library, so the app, the exposure log, and the
 * offline analysis always agree on who is in which arm.
 *
 * Variants are CONFIG PAYLOADS (src/constants/experiments.json): components
 * read config values from the payload and never branch on a variant name. The
 * control payload encodes current behavior, and getExperimentPayload resolves
 * to it whenever the master flag is off, the experiment is not `active`, or
 * there is no unit id — so flag-off rendering is byte-identical to today by
 * construction, with no dead-code paths.
 *
 * Exposure: trackExperimentExposure emits ONE `experiment_exposure` GTM event
 * per (experiment, unit) — the analysis runs on exposed units only — and emits
 * nothing unless the experiment is live (flag on + status active). Dedup uses
 * the same localStorage-marker pattern as the paid-signup conversion tracking.
 */
import EXPERIMENTS_CONFIG from '../constants/experiments.json';
import { gtmTrack } from './tracking';
import urls from '../constants/urlList';

// Master kill-switch. Per-experiment go-live is `status: "active"` in the
// config; both are required, and flips stay owner-gated per experiment.
export const experimentsEnabled = () =>
  /^(1|true|yes)$/i.test(process.env.REACT_APP_QLEARN_EXPERIMENTS || '');

export const EXPERIMENT_EXPOSURE_STORAGE_KEY = 'qlearn_experiment_exposures';

// Backend exposure-sink flag (exposure-sink ruling 2026-07-18, option b): the
// GTM-only emit left the analysis pipeline's integrity/SRM guardrails with no
// headlessly-readable exposure record. When this flag is on, the SAME event
// object also POSTs to the backend sink (dark server-side behind
// qlearn.experiment.exposure-sink-enabled — both ends must be on). Flag absent
// ⇒ this file's behavior is byte-identical to before the sink existed.
export const exposureSinkEnabled = () =>
  /^(1|true|yes)$/i.test(process.env.REACT_APP_EXPERIMENT_EXPOSURE_SINK || '');

/*
 * Fire-and-forget: exposure logging must NEVER break or slow the surface being
 * experimented on (the same posture as the localStorage marker guards). The
 * global fetch patch (store.js) attaches the bearer token; the server enforces
 * unitId == authenticated user and dedupes replays on its unique
 * (experiment_key, unit_id) index, so a lost/duplicate client marker can never
 * corrupt the sink. Errors are swallowed by design — GTM remains the emit of
 * record for the analytics family; the sink is the analyzable copy.
 */
const emitExposureToSink = (exposureEvent) => {
  if (!exposureSinkEnabled()) {
    return;
  }
  try {
    fetch(urls.experimentExposure, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(exposureEvent)
    }).catch(() => {});
  } catch (_) {
    // Even a synchronous fetch failure (no fetch in an exotic env) is non-fatal.
  }
};

// TTL for the per-(experiment, unit) exposure dedupe markers. Without a TTL the
// localStorage store grows unbounded (one entry per experiment a browser ever
// sees) — a client-persistence lifecycle-rule violation. 180 days is far longer
// than any single experiment's run (~49–86 days per the flip-readiness packet),
// so a live experiment never re-emits an exposure for a unit mid-run; it only
// bounds the store by pruning markers from long-concluded experiments.
export const EXPOSURE_MARKER_TTL_MS = 180 * 24 * 60 * 60 * 1000;

const BUCKETS = 10000;

// Minimal sync SHA-256 (UTF-8 string → hex). Sync so assignment is available
// on first render with no async flash; parity-locked against node crypto by
// the test vectors below (SubtleCrypto is async and unavailable on http).
const SHA256_K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
];

// UTF-8 encode without TextEncoder (absent in the jsdom test env).
const utf8Bytes = (message) => {
  const bytes = [];
  for (let i = 0; i < message.length; i += 1) {
    let code = message.codePointAt(i);
    if (code > 0xffff) i += 1; // surrogate pair consumed
    if (code < 0x80) {
      bytes.push(code);
    } else if (code < 0x800) {
      bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    } else if (code < 0x10000) {
      bytes.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    } else {
      bytes.push(
        0xf0 | (code >> 18), 0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f)
      );
    }
  }
  return bytes;
};

export const sha256Hex = (message) => {
  const rotr = (x, n) => (x >>> n) | (x << (32 - n));
  const H = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];

  const bytes = utf8Bytes(message);
  const bitLen = bytes.length * 8;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  for (let i = 7; i >= 0; i -= 1) bytes.push((bitLen / 2 ** (i * 8)) & 0xff);

  const w = new Array(64);
  for (let offset = 0; offset < bytes.length; offset += 64) {
    for (let i = 0; i < 16; i += 1) {
      w[i] = (bytes[offset + i * 4] << 24) | (bytes[offset + i * 4 + 1] << 16) |
        (bytes[offset + i * 4 + 2] << 8) | bytes[offset + i * 4 + 3];
    }
    for (let i = 16; i < 64; i += 1) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0;
    }
    let [a, b, c, d, e, f, g, h] = H;
    for (let i = 0; i < 64; i += 1) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + SHA256_K[i] + w[i]) | 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) | 0;
      h = g; g = f; f = e; e = (d + t1) | 0; d = c; c = b; b = a; a = (t1 + t2) | 0;
    }
    H[0] = (H[0] + a) | 0; H[1] = (H[1] + b) | 0; H[2] = (H[2] + c) | 0; H[3] = (H[3] + d) | 0;
    H[4] = (H[4] + e) | 0; H[5] = (H[5] + f) | 0; H[6] = (H[6] + g) | 0; H[7] = (H[7] + h) | 0;
  }
  return H.map((x) => (x >>> 0).toString(16).padStart(8, '0')).join('');
};

// Deterministic integer in [0, BUCKETS) — byte-identical to the analysis lib
// (first 13 hex chars = 52 bits, within safe-integer range).
export const bucketOf = (experimentKey, salt, unitId) =>
  Number.parseInt(sha256Hex(`${experimentKey}:${salt}:${String(unitId)}`).slice(0, 13), 16) % BUCKETS;

export const getExperiment = (experimentKey) =>
  (EXPERIMENTS_CONFIG.experiments || []).find((e) => e.key === experimentKey) || null;

const controlVariantOf = (experiment) =>
  experiment.variants.find((v) => v.name === 'control') || experiment.variants[0];

// Deterministic variant for a unit; null when there is no unit id (never default).
export const assignVariant = (experiment, unitId) => {
  if (unitId === null || unitId === undefined || String(unitId) === '') {
    return null;
  }
  const salt = String(experiment.salt ?? experiment.key);
  const totalWeight = experiment.variants.reduce((sum, v) => sum + (v.weight ?? 1), 0);
  const point = (bucketOf(experiment.key, salt, unitId) / BUCKETS) * totalWeight;
  let cumulative = 0;
  for (const variant of experiment.variants) {
    cumulative += variant.weight ?? 1;
    if (point < cumulative) {
      return variant.name;
    }
  }
  return experiment.variants[experiment.variants.length - 1].name; // floating-point safety net
};

// An experiment renders its assigned payload only when the master flag is on
// AND the experiment itself is active. Everything else = control = today.
const isLive = (experiment) => experimentsEnabled() && experiment.status === 'active';

/*
 * The single render entry point: components call this and read config values
 * off the returned payload — no variant names, no branches. Always returns a
 * payload (control when the experiment is unknown/off/dark or the unit has no
 * id), so callers need no fallback path of their own.
 */
export const getExperimentPayload = (experimentKey, unitId) => {
  const experiment = getExperiment(experimentKey);
  if (!experiment) {
    return {};
  }
  const control = controlVariantOf(experiment);
  if (!isLive(experiment)) {
    return control.payload || {};
  }
  const variantName = assignVariant(experiment, unitId);
  if (variantName === null) {
    return control.payload || {};
  }
  const variant = experiment.variants.find((v) => v.name === variantName) || control;
  return variant.payload || {};
};

const safeReadExposureMarkers = () => {
  try {
    const rawValue = window.localStorage.getItem(EXPERIMENT_EXPOSURE_STORAGE_KEY);
    return rawValue ? JSON.parse(rawValue) : {};
  } catch (_) {
    return {};
  }
};

// Normalize the marker store in place; returns whether anything changed so the
// caller can persist. Two cases:
//   - a parseable timestamp older than the TTL is DROPPED — bounds the store.
//     The TTL is >> any experiment's run, so a live experiment's own markers are
//     never aged out mid-flight (no spurious re-emit for an in-flight unit).
//   - a marker whose value can't be parsed as a timestamp is a legacy/malformed
//     entry, but the unit WAS exposed — so it is GRANDFATHERED: kept and
//     re-stamped with a fresh timestamp, never dropped. Dropping it would make an
//     already-exposed unit re-emit and double-count. (The only shipped format has
//     always been an ISO string, so no such entry exists in practice; this keeps
//     any future format migration re-emit-safe by construction.)
const normalizeExposureMarkers = (markers) => {
  const cutoff = Date.now() - EXPOSURE_MARKER_TTL_MS;
  const nowIso = new Date().toISOString();
  let changed = false;
  Object.keys(markers).forEach((key) => {
    const at = Date.parse(markers[key]);
    if (!Number.isFinite(at)) {
      markers[key] = nowIso; // grandfather: still "exposed", just re-stamped
      changed = true;
    } else if (at < cutoff) {
      delete markers[key];
      changed = true;
    }
  });
  return changed;
};

const safeWriteExposureMarkers = (markers) => {
  try {
    window.localStorage.setItem(EXPERIMENT_EXPOSURE_STORAGE_KEY, JSON.stringify(markers));
  } catch (_) {
    // Exposure dedupe must never break the surface being experimented on.
  }
};

/*
 * Emit `experiment_exposure` { experimentKey, variant, unitId, ts } once per
 * (experiment, unit) — call it at the moment the experiment-affected surface
 * first renders for the unit. No-ops (emits nothing, marks nothing) unless the
 * experiment is live, so dark experiments stay byte-silent.
 */
export const trackExperimentExposure = (experimentKey, unitId) => {
  const experiment = getExperiment(experimentKey);
  if (!experiment || !isLive(experiment)) {
    return false;
  }
  const variant = assignVariant(experiment, unitId);
  if (variant === null) {
    return false;
  }
  const markerKey = `${experimentKey}|${String(unitId)}`;
  const markers = safeReadExposureMarkers();
  const markersChanged = normalizeExposureMarkers(markers);
  if (markers[markerKey]) {
    // Already exposed (a fresh ISO marker, or a grandfathered legacy one).
    // Persist any normalization (expiry prune / legacy re-stamp) even on this
    // dedupe path so the change is durable.
    if (markersChanged) {
      safeWriteExposureMarkers(markers);
    }
    return false;
  }
  const exposureEvent = {
    experimentKey,
    variant,
    unitId: String(unitId),
    ts: new Date().toISOString()
  };
  gtmTrack('experiment_exposure', exposureEvent);
  emitExposureToSink(exposureEvent);
  markers[markerKey] = new Date().toISOString();
  safeWriteExposureMarkers(markers);
  return true;
};
